import React, { FC, useCallback, useEffect, useState } from 'react';
import { Node } from 'src/common/interfaces/node.interface';
import { useCurrentOrganization } from 'src/store/organization';
import { apiGetOrganizationTemplates, apiDeleteOrganizationTemplate } from 'src/utils/journeyApi';

import {
  DashboardHomeOrganizationTemplatesListHeaderSortType,
  DashboardLibraryOrganizationTemplatesList,
} from './list';

import { DashboardHomeOrganizationTemplatesNewJourneySlideover } from './new-journey-slideover';
import { useNotificationMessage } from 'src/common/notification/message.hook';
import { useFilterBarParams } from 'src/common/components/filter-bar/use-params';
import { useDepsMatchingCallbackRef } from 'src/utils/react/deps-matching-callback-ref.hook';
import { Helmet } from 'react-helmet';
import { FixedLoadingSpinner } from 'src/common/components/loading-spinner';
import { useCurrentSubscription } from 'src/store/subscription';
import OrganizationLink from 'src/common/OrganizationLink';
import { useFeatureRestrictions } from 'src/hooks/useFeatureRestrictions';

type DashboardLibraryOrganizationTemplatesProps = {};

export const DashboardLibraryOrganizationTemplates: FC<DashboardLibraryOrganizationTemplatesProps> = () => {
  const { currentOrganization, isFreeOrg } = useCurrentOrganization();
  const [loading, setLoading] = useState(false);
  const [templates, setTemplates] = useState<Node[]>([]);
  const { setSuccessNotification } = useNotificationMessage();

  const isStarter = useCurrentSubscription((state) => state.isStarter);
  const { features } = useFeatureRestrictions();
  
  const {
    filterBarParams: { search_term, sort_by, sort_order },
    updateSort,
  } = useFilterBarParams();

  const onFetchOrganizationTemplatesRef = useDepsMatchingCallbackRef(
    (response: any) => {
      setLoading(false);
      setTemplates(response.templates);
    },
    [sort_by, sort_order]
  );

  const fetchOrganizationTemplates = useCallback(async () => {
    setLoading(true);
    const response = await apiGetOrganizationTemplates({
      ...(search_term && { search_str: search_term }),
      ...(sort_by && { sort_by }),
      ...(sort_by && sort_order !== 'default' && { sort_order }),
    });
    onFetchOrganizationTemplatesRef.current && onFetchOrganizationTemplatesRef.current(response, [sort_by, sort_order]);
  }, [onFetchOrganizationTemplatesRef, search_term, sort_by, sort_order]);

  const onTableHeaderClick = useCallback((sortStack: DashboardHomeOrganizationTemplatesListHeaderSortType) => {
    updateSort(sortStack.sortName, sortStack.orderBy);
  }, []);

  const onDelete = useCallback(
    async (template: any) => {
      await apiDeleteOrganizationTemplate(currentOrganization.id, template.id);
      setTemplates(templates.filter((t) => t.id !== template.id));
      setSuccessNotification('Template deleted successfully');
    },
    [currentOrganization.id, templates, setSuccessNotification]
  );

  useEffect(() => {
    fetchOrganizationTemplates();
  }, [fetchOrganizationTemplates]);

  const renderContent = () => {
    if (loading) {
      return <FixedLoadingSpinner />;
    }
    if (templates.length > 0) {
      return (
        <DashboardLibraryOrganizationTemplatesList
          templates={templates}
          onDelete={onDelete}
          sort_by={sort_by}
          sort_order={sort_order}
          onTableHeaderClick={onTableHeaderClick}
        />
      );
    }
    // Empty
    if (search_term) {
      return <div className='text-bedrock-p text-bedrock-black'>No organization templates found</div>;
    }
    return (
      <>
      <div className='max-w-[640px] text-bedrock-p text-bedrock-black'>
        {`Organization Templates are Journeys that can be re-used over and over again. You can take any existing Journey
        and transform it into an Organization Template. Then, when creating a new Journey, you have the option to use
        this template as a starting point.${!features.canAccessOrganizationTemplates ? ` Upgrade to Scale Plan to use organization templates` : ``}`}
      </div>
      {
        !features.canAccessOrganizationTemplates &&
        <OrganizationLink  className="max-w-[640px] flex justify-start my-4" to='/billing'>
        <button className='btn-bedrock-primary self-start flex space-x-1'>
          <span>Upgrade to Scale Plan</span>
        </button>
    </OrganizationLink>
      }
      </>
    );
  };

  return (
    <div className='flex relative flex-col justify-between w-full space-y-md'>
      <Helmet>
        <title>Journey | Organization Templates</title>
      </Helmet>
      {renderContent()}
   
      <DashboardHomeOrganizationTemplatesNewJourneySlideover />
    </div>
  );
};
